import {Stack, Tooltip, Typography} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import {get, isEqual} from 'lodash';
import {FC, Fragment, MouseEvent, useRef} from 'react';
import {useStore} from 'zustand';
import {useGetDictsServiceTypesStatusesQuery} from '../../../../../../global/services/service-types';
import {PERIOD_NAMES, PERIOD_OPTIONS} from '../../constants/period';
import {renderPeriodValue} from '../../helpers';
import {usePrimaryChartDataGate} from '../../hooks/usePrimaryChartDataGate';
import {useStoresContext} from '../../hooks/useStoresContext';
import {usePrimaryChartsDataStore} from '../../store/usePrimaryChartsDataStore';
import {FallbackWithChildren} from '../FallbackWithChildren';
import {PeriodPicker, PeriodPickerRef} from '../PeriodPicker';
import {CommonChart, PartCharts} from './Charts';
import {preparePrimaryChartData} from './helpers';
import {STYLES} from './styles';
import {DashboardControlButtons} from '../DashboardControlButtons';
import {PeriodFilterTooltip} from '../PeriodFilterTooltip';

interface Props {
  serviceType: string;
}

export const PrimaryCharts: FC<Props> = ({serviceType}) => {
  usePrimaryChartDataGate(serviceType);

  const {chartViewStore, primaryChartsFiltersStore} = useStoresContext(serviceType);
  const {data: srStatuses} = useGetDictsServiceTypesStatusesQuery({});
  const periodPickerRef = useRef<PeriodPickerRef>(null);
  const contracts = usePrimaryChartsDataStore((s) => s.contracts);
  const isLoading = usePrimaryChartsDataStore((s) => s.isLoading);
  const {commonChart, partCharts} = preparePrimaryChartData(contracts, srStatuses);
  const isPrimaryFullWidth = useStore(chartViewStore, (s) => s.isPrimaryFullWidth());
  const {period, setPeriod, customPeriodEnd, customPeriodStart, setCustomPeriod, resetCustomPeriod} =
    useStore(primaryChartsFiltersStore);

  const handleCustomPeriodChange = (start: string, end: string) => {
    setCustomPeriod(start, end);
    setPeriod(PERIOD_NAMES.MANUAL);
  };

  const handlePeriodClick = (e: MouseEvent<HTMLDivElement>) => {
    const value = get(e, 'target.dataset.value');
    if (!value) return;
    if (isEqual(value, PERIOD_NAMES.MANUAL)) {
      periodPickerRef.current?.open();
      return;
    }
    setPeriod(value);
    resetCustomPeriod();
    periodPickerRef.current?.reset();
  };

  return (
    <Fragment>
      <Stack direction="row" alignItems="center" sx={STYLES.chartHeader}>
        <Typography sx={STYLES.chartTitle}>Движение заявок</Typography>
        <Tooltip placement="top" title={renderPeriodValue(customPeriodStart, customPeriodEnd)(period)}>
          <FormControl sx={{minWidth: 130}} size="small">
            <InputLabel id="period" sx={STYLES.chartSelectLabel}>
              Период
            </InputLabel>
            <Select
              key="primary_period"
              labelId="period"
              id="period"
              label="Период"
              value={period}
              onClick={handlePeriodClick}
              sx={STYLES.chartSelect}
              renderValue={renderPeriodValue(customPeriodStart, customPeriodEnd)}
            >
              {PERIOD_OPTIONS.map(({label, value}) => (
                <MenuItem key={`primary_${value}`} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Tooltip>

        <Stack direction="row" alignItems="center" gap={2} sx={STYLES.buttonsContainer}>
          <DashboardControlButtons serviceType={serviceType} />
        </Stack>
      </Stack>
      <Stack sx={STYLES.chartContainer}>
        <FallbackWithChildren isLoading={isLoading}>
          {!isPrimaryFullWidth ? <CommonChart commonChart={commonChart} /> : <PartCharts partCharts={partCharts} />}
          <PeriodPicker ref={periodPickerRef} onAccept={handleCustomPeriodChange} />
        </FallbackWithChildren>
      </Stack>
    </Fragment>
  );
};
